import type { CoachMode } from "@/lib/types";

// Config-driven free tasters. Each tool exposes a few curated, deliberately
// age-neutral prompts shown one at a time; the generic <FreeTaster> renders the
// flow and the per-prompt `message` reproduces exactly what that tool's
// evaluate coach mode expects (matched to the in-app runners).

interface TasterHint {
  label: string;
  value: string;
}

interface TasterPrompt {
  sourceLabel: string;
  source: string; // shown in mono; may contain newlines (whitespace-pre-line)
  hints: TasterHint[];
  exemplar: string; // a strong version, revealed on demand
  message: (answer: string) => string; // exact payload for the coach mode
}

export interface TasterConfig {
  slug: string;
  mode: CoachMode;
  title: string;
  intro: string;
  placeholder: string;
  prompts: TasterPrompt[];
}

// ---- Show, Don't Tell (mode: show-dont-tell-show-evaluate) ----
const sdt = (p: {
  tell: string;
  scene: string;
  showThrough: string;
  requirement: string;
  feel: string;
  exemplar: string;
}): TasterPrompt => ({
  sourceLabel: "The telling line",
  source: p.tell,
  hints: [
    { label: "The scene", value: p.scene },
    { label: "Show it through", value: p.showThrough },
    { label: "Make the reader feel", value: p.feel },
  ],
  exemplar: p.exemplar,
  message: (answer) =>
    `The telling line they had to replace: ${p.tell}
The scene: ${p.scene}
Channel requirement: ${p.requirement}
The feeling steer (a hint, not a strict target): ${p.feel}

The writer's rewrite:
${answer}`,
});

// ---- Strong Verbs (mode: strong-verbs-upgrade-evaluate) ----
const sv = (p: {
  line: string;
  weak: string[];
  requirement: string;
  exemplar: string;
}): TasterPrompt => ({
  sourceLabel: "The flat line",
  source: p.line,
  hints: [
    { label: "Weak verbs to replace", value: p.weak.join(", ") },
    { label: "Aim for", value: p.requirement },
  ],
  exemplar: p.exemplar,
  message: (answer) =>
    `The line to upgrade: ${p.line}
Weak verb(s): ${p.weak.join(", ")}
${p.requirement}

The writer's rewrite:
${answer}`,
});

// ---- Sentence Combining (mode: sentence-combining-evaluate) ----
const sc = (p: { sentences: string[]; exemplar: string }): TasterPrompt => ({
  sourceLabel: "The choppy sentences",
  source: p.sentences.join("\n"),
  hints: [
    { label: "Goal", value: "Combine these into one smooth sentence — no choppiness, no run-on." },
  ],
  exemplar: p.exemplar,
  message: (answer) =>
    `Original choppy sentences:
${p.sentences.map((s, i) => `${i + 1}. ${s}`).join("\n")}

The student's combined version:
${answer}`,
});

// ---- Word Power (mode: word-power-upgrade-evaluate) ----
const wp = (p: {
  sentence: string;
  weakWord: string;
  requirement: string;
  exemplar: string;
}): TasterPrompt => ({
  sourceLabel: "The flat sentence",
  source: p.sentence,
  hints: [
    { label: "Weak word to upgrade", value: p.weakWord },
    { label: "Aim for", value: p.requirement },
  ],
  exemplar: p.exemplar,
  message: (answer) =>
    `The sentence: ${p.sentence}
The weak word they had to upgrade: ${p.weakWord}
Requirement: ${p.requirement}

The writer's rewrite:
${answer}`,
});

// ---- Varied Openers (mode: varied-openers-choose-evaluate) ----
const vo = (p: {
  kernel: string;
  acceptableOpeners: string[];
  bestOpener: string;
  model: string;
}): TasterPrompt => ({
  sourceLabel: "The flat sentence (starts with the subject)",
  source: p.kernel,
  hints: [
    { label: "Openers that work well here", value: p.acceptableOpeners.join(", ") },
    { label: "Strongest choice", value: p.bestOpener },
  ],
  exemplar: p.model,
  message: (answer) =>
    `Flat sentence (subject-first): "${p.kernel}"
Openers that would genuinely improve it: ${p.acceptableOpeners.join(", ")}
The strongest choice here: ${p.bestOpener}
A hand-authored model rewrite: "${p.model}"
The student's rewrite:
${answer}`,
});

// ---- Figurative Language (mode: figurative-language-figure-evaluate) ----
const fl = (p: {
  moment: string;
  feel: string;
  figureHint: string;
  exemplar: string;
}): TasterPrompt => ({
  sourceLabel: "The moment to capture",
  source: p.moment,
  hints: [
    { label: "Figure to use", value: p.figureHint },
    { label: "Feeling to aim for", value: p.feel },
  ],
  exemplar: p.exemplar,
  message: (answer) =>
    `The moment: ${p.moment}
The feeling to aim for (a steer, not a strict target): ${p.feel}
Figure type they were asked to use: ${p.figureHint}

The writer's figure:
${answer}`,
});

// ---- Connectives (mode: sentence-connectives-evaluate) ----
const cc = (p: {
  s1: string;
  s2: string;
  relationship: string;
  suggested: string[];
  exemplar: string;
}): TasterPrompt => ({
  sourceLabel: "Two short sentences",
  source: `${p.s1}\n${p.s2}`,
  hints: [
    { label: "Relationship", value: p.relationship },
    { label: "Connectives that fit", value: p.suggested.join(", ") },
  ],
  exemplar: p.exemplar,
  message: (answer) =>
    `Sentence 1: "${p.s1}"
Sentence 2: "${p.s2}"
Relationship: ${p.relationship}
Suggested connectives: ${p.suggested.join(", ")}

The student's linked version:
${answer}`,
});

// ---- Voice Control (mode: voice-control-active-evaluate) ----
const VC_GOAL =
  "rewrite the sentence in the active voice so the doer leads and performs the action directly";
const vc = (p: { sentence: string; cue: string; model: string }): TasterPrompt => ({
  sourceLabel: "The passive sentence",
  source: p.sentence,
  hints: [
    { label: "Rewrite in", value: "the active voice — the doer leads" },
    { label: "Tip", value: p.cue },
  ],
  exemplar: p.model,
  message: (answer) =>
    `Move: Active
Goal: ${VC_GOAL}
Cue shown to the student: ${p.cue}
Hand-authored model answer (gold standard): ${p.model}

Original sentence:
${p.sentence}

The student's rewrite:
${answer}`,
});

// ---- Length Rhythm (mode: rhythm-move-evaluate) ----
const RH_GOAL = "add a very short sentence (2-4 words) for impact after a long one";
const rh = (p: { passage: string; cue: string; model: string }): TasterPrompt => ({
  sourceLabel: "An even, samey passage",
  source: p.passage,
  hints: [
    { label: "Move", value: "Punch — add a 2–4 word sentence for impact" },
    { label: "Tip", value: p.cue },
  ],
  exemplar: p.model,
  message: (answer) =>
    `Move: Punch
Goal: ${RH_GOAL}
Cue shown to the student: ${p.cue}
Hand-authored model answer (gold standard): ${p.model}

Original passage:
${p.passage}

The student's rewrite:
${answer}`,
});

// ---- Right Word (mode: right-word-evaluate) ----
const rw = (p: {
  sentence: string;
  context: string;
  options: string[];
  best: string;
}): TasterPrompt => ({
  sourceLabel: "The sentence (fill the blank)",
  source: p.sentence,
  hints: [
    { label: "Context", value: p.context },
    { label: "Choose from", value: p.options.join(", ") },
  ],
  // The full sentence with the right word slotted in — a meaningful "show me a
  // strong one" reveal, and (unlike the bare word) it won't trip the copied-model
  // guard, so the grader still runs and explains WHY the word fits.
  exemplar: p.sentence.replace("[BLANK]", p.best),
  message: (answer) =>
    `Sentence (with the blank): ${p.sentence}
Context that decides the precise word: ${p.context}
The near-synonyms on offer: ${p.options.join(", ")}

The word the student picked:
${answer}`,
});

const TASTERS: Record<string, TasterConfig> = {
  "show-dont-tell": {
    slug: "show-dont-tell",
    mode: "show-dont-tell-show-evaluate",
    title: "Show, don’t tell",
    intro:
      "Good writing makes the reader see the feeling instead of being told it. Rewrite the flat line below so it shows the feeling, and get instant coaching on your own words.",
    placeholder: "Write your showing version here…",
    prompts: [
      sdt({
        tell: "The room was tense.",
        scene: "A small team is waiting to hear who's being let go.",
        showThrough: "what people DO — their actions and bodies",
        requirement:
          "Show the tension through what people do — their actions and bodies — without ever naming the feeling.",
        feel: "nobody wants to be the first to speak",
        exemplar:
          "Two people checked phones that hadn't buzzed; a third clicked a pen until the clicking was the only sound in the room.",
      }),
      sdt({
        tell: "She was exhausted.",
        scene: "The end of a long double shift, still with the journey home to go.",
        showThrough: "her body and small actions",
        requirement:
          "Show the exhaustion through her body and what she does — without naming the feeling.",
        feel: "running on empty",
        exemplar:
          "She missed the lock twice, rested her forehead on the cold door, and let the bag slide off her shoulder onto the step.",
      }),
      sdt({
        tell: "He was nervous.",
        scene: "Waiting in the wings to walk on stage and give a speech.",
        showThrough: "small, involuntary actions",
        requirement:
          "Show the nerves through small, involuntary actions — not by naming the feeling.",
        feel: "wishing he could be anywhere else",
        exemplar:
          "He read the first line again, though he knew it cold, and pressed his damp palms flat against his trousers.",
      }),
    ],
  },

  "strong-verbs": {
    slug: "strong-verbs",
    mode: "strong-verbs-upgrade-evaluate",
    title: "Strong verbs",
    intro:
      "Weak, all-purpose verbs make writing flat. Swap them for vivid, precise ones — rewrite the line below and get instant coaching on your own words.",
    placeholder: "Write your upgraded line here…",
    prompts: [
      sv({
        line: "The dog went across the yard and got the ball.",
        weak: ["went", "got"],
        requirement: "Replace the weak verbs with vivid, precise ones that show how the dog moved.",
        exemplar: "The dog tore across the yard and snatched the ball.",
      }),
      sv({
        line: "She came into the room and looked at the mess.",
        weak: ["came", "looked at"],
        requirement: "Swap the flat verbs for ones that reveal her mood and energy.",
        exemplar: "She stormed into the room and glared at the mess.",
      }),
      sv({
        line: "The storm came and the trees moved in the wind.",
        weak: ["came", "moved"],
        requirement: "Replace the weak verbs with strong ones that make the storm feel powerful.",
        exemplar: "The storm rolled in and the trees thrashed in the wind.",
      }),
    ],
  },

  combining: {
    slug: "combining",
    mode: "sentence-combining-evaluate",
    title: "Sentence combining",
    intro:
      "Choppy, repetitive sentences slow a reader down. Combine the lines below into one smooth sentence and get instant coaching on your own words.",
    placeholder: "Write your combined sentence here…",
    prompts: [
      sc({
        sentences: ["The cat was small.", "The cat was black.", "It slept on the windowsill."],
        exemplar: "The small black cat slept on the windowsill.",
      }),
      sc({
        sentences: ["It was raining.", "We were cold.", "We went inside."],
        exemplar: "Cold and soaked by the rain, we went inside.",
      }),
      sc({
        sentences: ["The runner crossed the line.", "She was exhausted.", "The crowd cheered."],
        exemplar: "Exhausted, the runner crossed the line as the crowd cheered.",
      }),
    ],
  },

  "word-power": {
    slug: "word-power",
    mode: "word-power-upgrade-evaluate",
    title: "Word power",
    intro:
      "Flat, all-purpose words make writing forgettable. Swap one for a precise, vivid word — rewrite the line below and get instant coaching on your own words.",
    placeholder: "Write your upgraded line here…",
    prompts: [
      wp({
        sentence: "The food at the stall was nice.",
        weakWord: "nice",
        requirement: "Replace the vague word with one that says exactly what made the food good.",
        exemplar: "The food at the stall was smoky and salty, charred at the edges.",
      }),
      wp({
        sentence: "The little shop sold all kinds of stuff.",
        weakWord: "stuff",
        requirement:
          "Replace the vague word with a precise one that says exactly what the shop sold.",
        exemplar: "The little shop sold curios — old maps, brass keys, dusty glass jars.",
      }),
      wp({
        sentence: "It was a big storm.",
        weakWord: "big",
        requirement: "Replace the weak adjective with a precise, vivid one.",
        exemplar: "It was a ferocious storm.",
      }),
    ],
  },

  starters: {
    slug: "starters",
    mode: "varied-openers-choose-evaluate",
    title: "Varied openers",
    intro:
      "Starting every sentence with the subject gets monotonous. Open this one a fresh way — rewrite it below and get instant coaching on your own words.",
    placeholder: "Write your re-opened sentence here…",
    prompts: [
      vo({
        kernel: "The dog ran across the yard when it heard the gate.",
        acceptableOpeners: [
          "-ing opener (e.g. 'Hearing…')",
          "Subordinate-clause opener (e.g. 'When…')",
        ],
        bestOpener: "-ing opener (e.g. 'Hearing…')",
        model: "Hearing the gate, the dog ran across the yard.",
      }),
      vo({
        kernel: "She opened the box carefully because she wasn't sure what was inside.",
        acceptableOpeners: [
          "Adverb opener (e.g. 'Carefully,…')",
          "Subordinate-clause opener (e.g. 'Unsure…')",
        ],
        bestOpener: "Adverb opener (e.g. 'Carefully,…')",
        model: "Carefully, unsure what was inside, she opened the box.",
      }),
      vo({
        kernel: "The crowd fell silent as the lights went down.",
        acceptableOpeners: [
          "Subordinate-clause opener (e.g. 'As…')",
          "Prepositional-phrase opener",
        ],
        bestOpener: "Subordinate-clause opener (e.g. 'As…')",
        model: "As the lights went down, the crowd fell silent.",
      }),
    ],
  },

  "figurative-language": {
    slug: "figurative-language",
    mode: "figurative-language-figure-evaluate",
    title: "Figurative language",
    intro:
      "A fresh comparison makes a moment vivid; a tired one (busy as a bee) makes it invisible. Write one figure for the moment below and get instant coaching on your own words.",
    placeholder: "Write your simile or metaphor here…",
    prompts: [
      fl({
        moment: "A crowded train platform at rush hour.",
        feel: "overwhelming, no room to breathe",
        figureHint: "simile",
        exemplar:
          "The platform was as packed as a jar of pickles, everyone wedged shoulder to shoulder.",
      }),
      fl({
        moment: "The last light of sunset on a still lake.",
        feel: "calm, fading",
        figureHint: "metaphor",
        exemplar: "The lake was a sheet of cooling metal, the last light bleeding off its edge.",
      }),
      fl({
        moment: "An old house creaking on a windy night.",
        feel: "uneasy, alive",
        figureHint: "personification",
        exemplar: "The old house groaned and shifted, muttering to itself against the wind.",
      }),
    ],
  },

  connectives: {
    slug: "connectives",
    mode: "sentence-connectives-evaluate",
    title: "Connectives",
    intro:
      "Two short sentences side by side can feel choppy. Join them with the right connective — and the right punctuation — then get instant coaching on your own words.",
    placeholder: "Write your single linked sentence here…",
    prompts: [
      cc({
        s1: "The sky turned grey.",
        s2: "We packed up the picnic.",
        relationship: "cause and effect",
        suggested: ["because", "so", "as"],
        exemplar: "We packed up the picnic because the sky turned grey.",
      }),
      cc({
        s1: "She had never flown before.",
        s2: "She wasn't nervous at all.",
        relationship: "contrast",
        suggested: ["but", "although", "yet"],
        exemplar: "Although she had never flown before, she wasn't nervous at all.",
      }),
      cc({
        s1: "The alarm rang.",
        s2: "Everyone left the building.",
        relationship: "time / sequence",
        suggested: ["when", "after", "as soon as"],
        exemplar: "As soon as the alarm rang, everyone left the building.",
      }),
    ],
  },

  "voice-control": {
    slug: "voice-control",
    mode: "voice-control-active-evaluate",
    title: "Voice control",
    intro:
      "Passive sentences hide who did what. Rewrite the line below in the active voice so the doer leads — then get instant coaching on your own words.",
    placeholder: "Write your active-voice version here…",
    prompts: [
      vc({
        sentence: "The window was broken by the storm.",
        cue: "Who or what did it? Put them first.",
        model: "The storm broke the window.",
      }),
      vc({
        sentence: "The cake was eaten by the children.",
        cue: "Name the doer and let them act.",
        model: "The children ate the cake.",
      }),
      vc({
        sentence: "The new bridge was designed by a local engineer.",
        cue: "Start with the person who acted.",
        model: "A local engineer designed the new bridge.",
      }),
    ],
  },

  rhythm: {
    slug: "rhythm",
    mode: "rhythm-move-evaluate",
    title: "Length rhythm",
    intro:
      "When every sentence is the same length, writing drones. Break this passage with one short, punchy sentence for impact — then get instant coaching on your own words.",
    placeholder: "Rewrite the passage with a punch here…",
    prompts: [
      rh({
        passage:
          "The waves rolled in one after another, steady and grey, and the long beach stretched on without a single person in sight for what felt like miles.",
        cue: "End on a 2–4 word punch.",
        model:
          "The waves rolled in one after another, steady and grey, and the long beach stretched on without a single person in sight. Nothing moved.",
      }),
      rh({
        passage:
          "He checked the lock twice, tested the handle, walked back to the car, and then stood there wondering whether he had remembered to turn off the stove at all.",
        cue: "Add a short punch for impact.",
        model:
          "He checked the lock twice, tested the handle, and walked back to the car. Then he froze.",
      }),
      rh({
        passage:
          "The crowd pushed toward the stage as the lights swept across the arena and the first long chord rang out over the roar of ten thousand voices.",
        cue: "Drop in a very short sentence.",
        model:
          "The crowd pushed toward the stage as the lights swept across the arena and the first long chord rang out over the roar of ten thousand voices. The floor shook.",
      }),
    ],
  },

  "right-word": {
    slug: "right-word",
    mode: "right-word-evaluate",
    title: "Right word",
    intro:
      "The biggest word is rarely the best word. Read the context, pick the one word that fits exactly — then get instant coaching on why it lands.",
    placeholder: "Type the word that fits best…",
    prompts: [
      rw({
        sentence: "The prisoner's face was [BLANK] after months without food.",
        context: "A starving prisoner who is unhealthily thin — almost skeletal.",
        options: ["slim", "slender", "gaunt", "lean"],
        best: "gaunt",
      }),
      rw({
        sentence: "She [BLANK] through the curtains to see who was at the door.",
        context: "She is trying to see without being seen — careful, hidden, secretive.",
        options: ["looked", "glanced", "peered", "stared"],
        best: "peered",
      }),
      rw({
        sentence: "The students were [BLANK] about the surprise excursion.",
        context:
          "They had hoped for it all term — this is overwhelming joy, not just mild contentment.",
        options: ["happy", "ecstatic", "pleased", "glad"],
        best: "ecstatic",
      }),
    ],
  },
};

export function getTaster(slug: string): TasterConfig | undefined {
  return TASTERS[slug];
}
